import { useQuery } from '@apollo/client/react/index.js';
import {
  AnimatedCounter,
  AssigneePills,
  cn,
  GlassBadge,
  GlassButton,
  GlassCard,
  GlassCardContent,
  GlassEmptyState,
  GlassInput,
  GlassSelect,
  GlassTable,
  GlassTableBody,
  GlassTableCell,
  GlassTableHead,
  GlassTableHeaderCell,
  GlassTableRow,
  normalizeServiceOrderStatus,
  SERVICE_ORDER_FILTER_OPTIONS,
  SERVICE_ORDER_STATUS_LABEL,
  SERVICE_ORDER_STATUS_VARIANT,
  SkeletonLoader,
} from '@merritrac/shared/components';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ClipboardList,
  LayoutList,
  Plus,
  Search,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { GET_SERVICE_ORDERS } from '../lib/graphql/operations.js';

import type { ServiceOrderStatus } from '@merritrac/shared/components';
import type { LucideIcon } from 'lucide-react';
import type { ReactElement } from 'react';

/* ── Types ── */

interface ServiceOrderClient {
  clientCode: string;
  clientName: string;
  city: string | null;
  state: string | null;
}

interface ServiceOrderNode {
  accessKey: number;
  ticketno: string | null;
  description: string | null;
  status: string | null;
  priority: string | null;
  assignedTo: string | null;
  clientCode: string | null;
  createdDate: string | null;
  dueDate: string | null;
  completedDate: string | null;
  client: ServiceOrderClient | null;
  jobType: string | null;
  jobSite: string | null;
}

interface ServiceOrderEdge {
  node: ServiceOrderNode;
}

interface ServiceOrderConnection {
  totalCount: number;
  pageInfo: { hasNextPage: boolean; endCursor: string | null };
  edges: ServiceOrderEdge[];
}

interface GetServiceOrdersData {
  serviceOrders: ServiceOrderConnection;
}

/* ── Helpers ── */

function formatDate(raw: string | null): string {
  if (raw === null || raw === '') {
    return '\u2014';
  }
  const d = new Date(raw);
  if (isNaN(d.getTime())) {
    return raw;
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/* ── KPI Stat Card ── */

interface StatCardProps {
  label: string;
  value: number;
  icon: LucideIcon;
  iconClassName: string;
  bgClassName: string;
  loading?: boolean;
}

function StatCard({
  label,
  value,
  icon: Icon,
  iconClassName,
  bgClassName,
  loading,
}: StatCardProps): ReactElement {
  return (
    <GlassCard hover>
      <GlassCardContent className="flex items-center gap-3 py-3">
        <div
          className={cn(
            'flex items-center justify-center w-9 h-9 rounded-lg shrink-0',
            bgClassName,
          )}
        >
          <Icon size={16} className={iconClassName} />
        </div>
        <div className="min-w-0">
          {loading === true ? (
            <SkeletonLoader variant="block" width="48px" height="24px" />
          ) : (
            <AnimatedCounter value={value} className="text-xl font-bold text-text leading-none" />
          )}
          <p className="text-[11px] text-text-secondary mt-0.5 truncate">{label}</p>
        </div>
      </GlassCardContent>
    </GlassCard>
  );
}

/* ── Page ── */

export function ServiceOrdersPage(): ReactElement {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('in-progress');

  const { data, loading, error } = useQuery<GetServiceOrdersData>(GET_SERVICE_ORDERS, {
    variables: {
      first: 50,
      status: statusFilter === 'All' ? undefined : statusFilter,
    },
  });

  const orders: ServiceOrderNode[] = data?.serviceOrders.edges.map((e) => e.node) ?? [];

  const filtered = orders.filter((node) => {
    const query = search.toLowerCase();
    if (query === '') {
      return true;
    }
    const fields = [
      node.ticketno,
      node.client?.clientName,
      node.jobSite,
      node.assignedTo,
      node.jobType,
      node.description,
    ];
    return fields.some((f) => f?.toLowerCase().includes(query) === true);
  });

  const totalOrders = data?.serviceOrders.totalCount ?? orders.length;
  const activeCount = orders.filter(
    (o) => normalizeServiceOrderStatus(o.status) === 'in-progress',
  ).length;
  const completedCount = orders.filter(
    (o) => normalizeServiceOrderStatus(o.status) === 'completed',
  ).length;
  const overdueCount = orders.filter(
    (o) => normalizeServiceOrderStatus(o.status) === 'overdue',
  ).length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-text-secondary">Manage and track all field service orders</p>
        <GlassButton
          variant="primary"
          size="md"
          onClick={() => {
            void navigate('/service-orders/new');
          }}
        >
          <Plus size={16} />
          New Order
        </GlassButton>
      </div>

      {/* KPI Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          label="Total Orders"
          value={totalOrders}
          icon={LayoutList}
          iconClassName="text-primary"
          bgClassName="bg-primary-soft"
          loading={loading}
        />
        <StatCard
          label="Active"
          value={activeCount}
          icon={Activity}
          iconClassName="text-success-fg"
          bgClassName="bg-success-soft"
          loading={loading}
        />
        <StatCard
          label="Completed"
          value={completedCount}
          icon={CheckCircle2}
          iconClassName="text-info-fg"
          bgClassName="bg-info-soft"
          loading={loading}
        />
        <StatCard
          label="Overdue"
          value={overdueCount}
          icon={AlertTriangle}
          iconClassName="text-danger-fg"
          bgClassName="bg-danger-soft"
          loading={loading}
        />
      </div>

      {/* Filters */}
      <GlassCard>
        <GlassCardContent className="py-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-sm font-semibold text-text">All Orders</h2>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none z-10"
                />
                <GlassInput
                  placeholder="Search orders…"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                  }}
                  className="pl-8 w-full sm:w-56"
                  aria-label="Search service orders"
                />
              </div>
              <GlassSelect
                options={SERVICE_ORDER_FILTER_OPTIONS}
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                }}
                aria-label="Filter by status"
                className="w-full sm:w-36"
              />
            </div>
          </div>
        </GlassCardContent>
      </GlassCard>

      {/* Table */}
      {loading ? (
        <GlassCard>
          <div className="p-4 space-y-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <SkeletonLoader key={i} variant="block" height="40px" />
            ))}
          </div>
        </GlassCard>
      ) : error !== undefined ? (
        <GlassCard>
          <div className="py-12 text-center text-sm text-danger">
            Failed to load: {error.message}
          </div>
        </GlassCard>
      ) : filtered.length === 0 ? (
        <GlassCard className="py-8">
          <GlassEmptyState
            icon={ClipboardList}
            title="No orders found"
            description={
              search !== '' || statusFilter !== 'All'
                ? 'Try adjusting your search or filter.'
                : 'No service orders yet. Create one to get started.'
            }
          />
        </GlassCard>
      ) : (
        <>
          <div className="overflow-hidden rounded-xl">
            <GlassTable className="table-fixed w-full">
              <GlassTableHead>
                <GlassTableRow>
                  <GlassTableHeaderCell className="w-[108px]">Order</GlassTableHeaderCell>
                  <GlassTableHeaderCell className="w-[28%]">Client</GlassTableHeaderCell>
                  <GlassTableHeaderCell className="w-[14%]">Type</GlassTableHeaderCell>
                  <GlassTableHeaderCell className="w-[80px]">Status</GlassTableHeaderCell>
                  <GlassTableHeaderCell>Assignee</GlassTableHeaderCell>
                  <GlassTableHeaderCell className="w-[100px] text-right">Date</GlassTableHeaderCell>
                </GlassTableRow>
              </GlassTableHead>
              <GlassTableBody>
                {filtered.map((node) => {
                  const orderId = node.ticketno ?? String(node.accessKey);
                  const clientName = node.jobSite ?? node.client?.clientName ?? '\u2014';
                  const status: ServiceOrderStatus = normalizeServiceOrderStatus(node.status);
                  const serviceDate = formatDate(node.dueDate ?? node.createdDate ?? '');

                  return (
                    <GlassTableRow
                      key={node.accessKey}
                      className="cursor-pointer hover:bg-surface-raised/50 transition-colors"
                      onClick={() => {
                        void navigate(`/service-orders/${String(node.accessKey)}`);
                      }}
                    >
                      <GlassTableCell>
                        <span className="font-medium text-primary text-xs font-mono">
                          {orderId}
                        </span>
                      </GlassTableCell>
                      <GlassTableCell className="truncate" title={clientName}>
                        <span className="font-medium text-text">{clientName}</span>
                      </GlassTableCell>
                      <GlassTableCell className="truncate" title={node.jobType ?? ''}>
                        {node.jobType !== null ? (
                          <span className="text-xs text-text-secondary">{node.jobType}</span>
                        ) : (
                          <span className="text-text-tertiary">\u2014</span>
                        )}
                      </GlassTableCell>
                      <GlassTableCell>
                        <GlassBadge variant={SERVICE_ORDER_STATUS_VARIANT[status]} dot>
                          {SERVICE_ORDER_STATUS_LABEL[status]}
                        </GlassBadge>
                      </GlassTableCell>
                      <GlassTableCell>
                        <AssigneePills names={node.assignedTo} />
                      </GlassTableCell>
                      <GlassTableCell className="text-text-tertiary text-xs whitespace-nowrap text-right">
                        {serviceDate}
                      </GlassTableCell>
                    </GlassTableRow>
                  );
                })}
              </GlassTableBody>
            </GlassTable>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-1">
            <p className="text-xs text-text-tertiary">
              Showing {filtered.length} of {totalOrders} orders
            </p>
            <GlassBadge>{statusFilter === 'All' ? 'All statuses' : statusFilter}</GlassBadge>
          </div>
        </>
      )}
    </div>
  );
}
